import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonButtons,
  IonMenuButton,
  IonTitle,
  IonContent,
  IonLoading,
} from "@ionic/react";
import {
  Box,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  InputAdornment,
  IconButton,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router";
import { requestID, baseUrl, getToken } from "../../common/utils";
import { formattedDate } from "../../common/utils_t";
import { useBusinessContext } from "../../context/business-context";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  AccountBalance as AccountBalanceIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
} from "@mui/icons-material";
import "./InconsistentSavers.css";

const InconsistentSavers = () => {
  const { name } = useParams<{ name: string }>();
  const { selectedBusiness } = useBusinessContext();
  
  const [searchQuery, setSearchQuery] = useState("");
  const [reportData, setReportData] = useState<any[]>([]);
  const [status, setStatus] = useState({ loading: false, error: false });
  const [stats, setStats] = useState({
    totalMembers: 0,
    totalSavings: 0,
    inconsistentCount: 0
  });

  const filteredData = reportData.filter(
    (row) =>
      row.uname?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      row.phone?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      row.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const onGetInconsistentSavers = async () => {
    if (!selectedBusiness?.business_id) return;

    const token = getToken();
    if (!token) return;

    setStatus({ loading: true, error: false });
    
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("business_id", selectedBusiness.business_id);
      formData.append("branch_id", selectedBusiness.branch_id);

      const currentConfig = {
        headers: {
          Authorization: `Bearer ${token}`,
          "content-type": "multipart/form-data",
        },
      };

      const response = await axios.post(
        baseUrl() + "users/inconsistentSavers_caas",
        formData,
        currentConfig
      );
      
      if (response.data.status === true) {
        const data = response.data.data || [];
        setReportData(data);
        
        // Calculate stats
        const totalSavings = data.reduce((sum: number, row: any) => 
          sum + Number(row.savingAmount || 0), 0);
        
        setStats({
          totalMembers: data.length,
          totalSavings,
          inconsistentCount: data.length
        });
      }
    } catch (error) {
      console.error("Error:", error);
      setStatus({ loading: false, error: true });
    } finally {
      setStatus({ loading: false, error: false });
    }
  };

  useEffect(() => {
    if (selectedBusiness?.business_id) {
      onGetInconsistentSavers();
    }
  }, [selectedBusiness]);

  {/*if (!selectedBusiness) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonMenuButton />
            </IonButtons>
            <IonTitle>{name}</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <Box className="loading-container">
            <CircularProgress />
            <Typography variant="h6" className="loading-text">
              Loading business context...
            </Typography>
          </Box>
        </IonContent>
      </IonPage>
    );
  }
*/}
  return (
    <IonPage className="pushDown">
      <IonHeader>
        <IonToolbar className="inconsistent-header">
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>
          <IonTitle>
            <Box className="header-content">
              <AccountBalanceIcon className="header-icon" />
              <Typography variant="h6" className="header-title">
                INCONSISTENT SAVERS
              </Typography>
            </Box>
          </IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen>
        <Container className="inconsistent-container">
          {/* Page Header */}
          <Box className="page-header">
            <AccountBalanceIcon className="page-icon" />
            <Typography variant="h4" className="page-title">
              Inconsistent Savers Report
            </Typography>
            <Typography variant="body1" className="page-subtitle">
              Members with irregular saving patterns
            </Typography>
          </Box>

          {/* Statistics Cards */}
          <Grid container spacing={3} className="stats-grid">
            <Grid item xs={12} md={4}>
              <Card className="stat-card total-members">
                <CardContent>
                  <PeopleIcon className="stat-icon" />
                  <Typography variant="h3" className="stat-number">
                    {stats.totalMembers}
                  </Typography>
                  <Typography variant="body2" className="stat-label">
                    Total Inconsistent Savers
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Card className="stat-card total-savings">
                <CardContent>
                  <AccountBalanceIcon className="stat-icon" />
                  <Typography variant="h3" className="stat-number">
                    ₦{stats.totalSavings.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" className="stat-label">
                    Total Savings Balance
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Card className="stat-card inconsistent-card">
                <CardContent>
                  <TrendingUpIcon className="stat-icon" />
                  <Typography variant="h3" className="stat-number">
                    {stats.inconsistentCount}
                  </Typography>
                  <Typography variant="body2" className="stat-label">
                    Needs Attention
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Search and Controls */}
          <Paper elevation={2} className="controls-paper">
            <Box className="controls-content">
              <TextField
                className="search-field"
                placeholder="Search members by name, email or phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                fullWidth
              />
              
              <IconButton
                className="refresh-button"
                onClick={onGetInconsistentSavers}
                disabled={status.loading}
              >
                <RefreshIcon />
              </IconButton>
            </Box>
          </Paper>

          {/* Results Section */}
          {status.loading ? (
            <Box className="loading-container">
              <CircularProgress />
              <Typography variant="h6">Loading report data...</Typography>
            </Box>
          ) : filteredData.length > 0 ? (
            <Paper elevation={2} className="results-paper">
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell className="table-header">S/N</TableCell>
                      <TableCell className="table-header">Member</TableCell>
                      <TableCell className="table-header">Contact</TableCell>
                      <TableCell className="table-header">Savings</TableCell>
                      <TableCell className="table-header">Joined</TableCell>
                      <TableCell className="table-header">Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredData.map((row, index) => (
                      <TableRow key={index} className="table-row">
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>
                          <Box className="member-cell">
                            <Typography className="member-name">
                              {row.uname || 'N/A'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box className="contact-cell">
                            <Typography variant="body2" className="email">
                              {row.email || 'N/A'}
                            </Typography>
                            <Typography variant="body2" className="phone">
                              {row.phone || 'N/A'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box className="savings-cell">
                            <Typography variant="body2" className="savings-amount">
                              ₦{Number(row.savingAmount || 0).toLocaleString()}
                            </Typography>
                            <Typography variant="caption" className="savings-balance">
                              Bal: ₦{Number(row.savingBal || 0).toLocaleString()}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formattedDate(row.dateCreated) || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label="Inconsistent"
                            color="warning"
                            size="small"
                            className="status-chip"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          ) : (
            <Paper elevation={2} className="empty-paper">
              <Box className="empty-content">
                <AccountBalanceIcon className="empty-icon" />
                <Typography variant="h6" className="empty-title">
                  No Inconsistent Savers Found
                </Typography>
                <Typography variant="body2" className="empty-subtitle">
                  {reportData.length === 0 
                    ? "No data available for the selected business."
                    : "No members match your search criteria."
                  }
                </Typography>
              </Box>
            </Paper>
          )}
        </Container>

        <IonLoading isOpen={status.loading} message="Processing..." />
      </IonContent>
    </IonPage>
  );
};

export default InconsistentSavers;